import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/lib/api';
import { X, Bell, Loader2, AlertTriangle } from 'lucide-react';

const EVENT_TYPES = [
  { value: 'teaching', label: 'Teaching / Lessons' },
  { value: 'tournament', label: 'Tournaments' },
  { value: 'casual', label: 'Casual play' },
  { value: 'special', label: 'Special events' },
];

const DISTANCE_OPTIONS = [10, 25, 50, 100, 250];

/**
 * Admin-side modal for managing a user's notification preferences.
 *
 * Opened from the User Management table. Loads the full user via
 * `GET /api/admin/users/:id` so we have the freshest prefs (admin pages
 * may show stale rows), then PATCHes back via `PUT /api/admin/users/:id`.
 *
 * Per product spec: admin changes are silent (no audit log, no email
 * to the user). UI structured as tabs so we can expand later
 * (e.g., Messaging, Privacy) without restructuring.
 */
export default function AdminUserNotificationsModal({ userId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);

  // Local edit state — all notification-relevant fields.
  const [phone, setPhone] = useState('');
  const [emailAlerts, setEmailAlerts] = useState(false);
  const [smsAlerts, setSmsAlerts] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [lastMinuteAlerts, setLastMinuteAlerts] = useState(false);
  const [eventAlertsEnabled, setEventAlertsEnabled] = useState(true);
  const [eventAlertsDistance, setEventAlertsDistance] = useState(50);
  const [eventAlertsTypes, setEventAlertsTypes] = useState(['teaching', 'tournament', 'casual', 'special']);
  const [newsletterOptOut, setNewsletterOptOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/users/${userId}`);
        if (cancelled) return;
        const u = data.user || {};
        setUser(u);
        setPhone(u.phone || '');
        const prefs = Array.isArray(u.alert_preferences) ? u.alert_preferences : [];
        setEmailAlerts(prefs.includes('email'));
        setSmsAlerts(prefs.includes('sms'));
        setSmsConsent(!!u.sms_consent);
        setLastMinuteAlerts(!!u.last_minute_alerts);
        setEventAlertsEnabled(u.event_alerts_enabled !== false);
        setEventAlertsDistance(Number(u.event_alerts_distance) || 50);
        const types = Array.isArray(u.event_alerts_types) && u.event_alerts_types.length > 0
          ? u.event_alerts_types
          : ['teaching', 'tournament', 'casual', 'special'];
        setEventAlertsTypes(types);
        setNewsletterOptOut(!!u.newsletter_opt_out);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load user');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, onClose]);

  const toggleEventType = (t) => {
    setEventAlertsTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const unsubscribeFromEverything = () => {
    setEmailAlerts(false);
    setSmsAlerts(false);
    setSmsConsent(false);
    setLastMinuteAlerts(false);
    setEventAlertsEnabled(false);
    setNewsletterOptOut(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Build alert_preferences from the two boolean toggles. Other
      // legacy values (e.g., 'manual') are preserved if the user had them.
      const existingOther = (user?.alert_preferences || []).filter(p => p !== 'email' && p !== 'sms');
      const alertPrefs = [
        ...existingOther,
        ...(emailAlerts ? ['email'] : []),
        ...(smsAlerts && smsConsent && phone.trim() ? ['sms'] : []),
      ];
      const payload = {
        phone: phone.trim(),
        alert_preferences: alertPrefs,
        sms_consent: !!smsConsent && !!phone.trim(),
        last_minute_alerts: !!lastMinuteAlerts,
        event_alerts_enabled: !!eventAlertsEnabled,
        event_alerts_distance: Number(eventAlertsDistance) || 50,
        event_alerts_types: eventAlertsTypes,
        newsletter_opt_out: !!newsletterOptOut,
      };
      const { data } = await api.put(`/admin/users/${userId}`, payload);
      toast.success('Notification preferences updated');
      onSaved?.(data.user || { id: userId, ...payload });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const smsBlockedByPhone = smsAlerts && !phone.trim();
  const smsBlockedByConsent = smsAlerts && !smsConsent;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-notif-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="admin-notif-modal"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#56c6df]/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#56c6df]" />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h2>
              <p className="text-xs text-slate-500" data-testid="admin-notif-target-email">
                {loading ? 'Loading…' : (user?.email || 'Unknown')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100"
            data-testid="admin-notif-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab strip — only one tab today, but the structure is in
            place so we can grow the user-detail modal later. */}
        <div className="border-b border-slate-100 px-6 pt-3">
          <div className="inline-flex gap-1" data-testid="admin-notif-tabs">
            <span className="px-3 py-2 text-sm font-medium border-b-2 border-[#56c6df] text-[#0f766e]">
              Notifications
            </span>
          </div>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Phone number — needed for SMS to function */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Phone number</Label>
              <Input
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="e.g. 555-123-4567"
                className="rounded-xl max-w-sm"
                data-testid="admin-notif-phone"
              />
              <p className="text-xs text-slate-400 mt-1">
                Required for SMS alerts. Clearing this disables SMS automatically.
              </p>
            </div>

            {/* Channels */}
            <div className="space-y-2">
              <Label className="text-sm font-medium mb-1 block">Alert channels</Label>
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${emailAlerts ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200'}`}>
                <Checkbox checked={emailAlerts} onCheckedChange={v => setEmailAlerts(!!v)} className="mt-0.5" data-testid="admin-notif-email" />
                <div>
                  <p className="font-medium text-sm">Email alerts</p>
                  <p className="text-xs text-slate-500">Event reminders, requests, and community notifications by email.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${smsAlerts ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200'}`}>
                <Checkbox checked={smsAlerts} onCheckedChange={v => setSmsAlerts(!!v)} className="mt-0.5" data-testid="admin-notif-sms" />
                <div>
                  <p className="font-medium text-sm">SMS alerts</p>
                  <p className="text-xs text-slate-500">Text messages sent to the phone number above.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${smsConsent ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200'}`}>
                <Checkbox checked={smsConsent} onCheckedChange={v => setSmsConsent(!!v)} className="mt-0.5" data-testid="admin-notif-sms-consent" />
                <div>
                  <p className="font-medium text-sm">SMS consent on file</p>
                  <p className="text-xs text-slate-500">CTIA / Twilio compliance flag — must be true for SMS to actually send.</p>
                </div>
              </label>
              {(smsBlockedByPhone || smsBlockedByConsent) && (
                <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    SMS alerts won't actually send: {smsBlockedByPhone && 'phone number is empty'}
                    {smsBlockedByPhone && smsBlockedByConsent && ' and '}
                    {smsBlockedByConsent && 'SMS consent is off'}.
                  </span>
                </div>
              )}
            </div>

            {/* Last-minute alerts */}
            <div>
              <Label className="text-sm font-medium mb-1 block">Last-minute games</Label>
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${lastMinuteAlerts ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200'}`}>
                <Checkbox checked={lastMinuteAlerts} onCheckedChange={v => setLastMinuteAlerts(!!v)} className="mt-0.5" data-testid="admin-notif-lastminute" />
                <div>
                  <p className="font-medium text-sm">Alert me for last-minute games</p>
                  <p className="text-xs text-slate-500">Hot signal — when a player nearby needs someone for a same-day game.</p>
                </div>
              </label>
            </div>

            {/* Event alerts */}
            <div>
              <Label className="text-sm font-medium mb-1 block">New-event alerts</Label>
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${eventAlertsEnabled ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200'}`}>
                <Checkbox checked={eventAlertsEnabled} onCheckedChange={v => setEventAlertsEnabled(!!v)} className="mt-0.5" data-testid="admin-notif-events-enabled" />
                <div>
                  <p className="font-medium text-sm">Send new-event digests</p>
                  <p className="text-xs text-slate-500">Daily digest of new events matching the filters below.</p>
                </div>
              </label>
              {eventAlertsEnabled && (
                <div className="mt-3 pl-2 space-y-3">
                  <div>
                    <Label className="text-xs mb-1.5 block">Distance radius</Label>
                    <div className="flex flex-wrap gap-2">
                      {DISTANCE_OPTIONS.map(d => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setEventAlertsDistance(d)}
                          className={`px-3 py-1.5 rounded-full text-xs border transition-all ${eventAlertsDistance === d ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid={`admin-notif-distance-${d}`}
                        >
                          {d} miles
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs mb-1.5 block">Event types</Label>
                    <div className="flex flex-wrap gap-2">
                      {EVENT_TYPES.map(t => (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => toggleEventType(t.value)}
                          className={`px-3 py-1.5 rounded-full text-xs border transition-all ${eventAlertsTypes.includes(t.value) ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid={`admin-notif-evtype-${t.value}`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Newsletter opt-out */}
            <div>
              <Label className="text-sm font-medium mb-1 block">Marketing & newsletter</Label>
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${newsletterOptOut ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`}>
                <Checkbox checked={newsletterOptOut} onCheckedChange={v => setNewsletterOptOut(!!v)} className="mt-0.5" data-testid="admin-notif-newsletter-optout" />
                <div>
                  <p className="font-medium text-sm">Opt out of newsletter / marketing emails</p>
                  <p className="text-xs text-slate-500">Honored by the admin newsletter composer. Transactional emails (password reset, receipts) still send.</p>
                </div>
              </label>
            </div>

            {/* Bulk action */}
            <div className="pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={unsubscribeFromEverything}
                className="text-xs text-red-600 hover:text-red-700 underline underline-offset-2"
                data-testid="admin-notif-unsub-all"
              >
                Unsubscribe from everything
              </button>
            </div>
          </div>
        )}

        <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2 sticky bottom-0 bg-white rounded-b-2xl">
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="admin-notif-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={loading || saving}
            className="rounded-full bg-[#56c6df] text-white hover:bg-[#3ba8c2] px-6"
            data-testid="admin-notif-save-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
